import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Calculator from "src/components/calculator";

// ─── Denominaciones EUR ─────────────────────────────────────────────
// Mismo set y mismo orden que `ArqueoCount` (cierre) para que apertura
// y cierre casen 1:1 en el ticket Z y visualmente sean idénticos.
// Los ficheros reales usan coma decimal (`0,50.png`), NO punto. Los
// centavos van con 2 dígitos (`0,10.png` no `0,1.png`).
const DENOMINATIONS = [
  { value: 200, img: "/static/caja/200.png", label: "200€" },
  { value: 100, img: "/static/caja/100.png", label: "100€" },
  { value: 50, img: "/static/caja/50.png", label: "50€" },
  { value: 20, img: "/static/caja/20.png", label: "20€" },
  { value: 10, img: "/static/caja/10.png", label: "10€" },
  { value: 5, img: "/static/caja/5.png", label: "5€" },
  { value: 2, img: "/static/caja/2.png", label: "2€" },
  { value: 1, img: "/static/caja/1.png", label: "1€" },
  { value: 0.5, img: "/static/caja/0,50.png", label: "50c" },
  { value: 0.2, img: "/static/caja/0,20.png", label: "20c" },
  { value: 0.1, img: "/static/caja/0,10.png", label: "10c" },
  { value: 0.05, img: "/static/caja/0,05.png", label: "5c" },
  { value: 0.02, img: "/static/caja/0,02.png", label: "2c" },
  { value: 0.01, img: "/static/caja/0,01.png", label: "1c" },
];

/**
 * Diálogo de conteo por denominaciones al ABRIR caja.
 *
 * Layout replicado de `ArqueoCount` (cierre):
 *   - 2 columnas: denominaciones (izq, split billetes/monedas) +
 *     calculadora sticky (der).
 *   - Fila seleccionable — el teclado numérico de la calculadora
 *     rellena la fila activa (mismo patrón del cierre).
 *   - Barra de acciones fija abajo con TOTAL y botones.
 *
 * Diferencias respecto al cierre: no hay descuadre (no hay ventas
 * previas), no hay paso "Confirmación", no hay checkbox de email.
 *
 * `onConfirm({ billetes, total })` — el caller decide qué hacer con
 * el payload (típicamente disparar `initialMoney` con el desglose).
 */
export default function OpeningCountDialog({
  open,
  onClose,
  onConfirm,
  // Lista opcional [{ value, unidades }] para precargar el diálogo
  // con un conteo previo — típicamente `arqueo.openingBilletes` cuando
  // el camarero re-abre el modal para revisar/ajustar el desglose
  // guardado en el mismo día. Sin este prop se arranca todo a 0.
  initialBilletes = null,
}) {
  // Construye el array de trabajo partiendo de las 14 denominaciones
  // y mergeando `unidades` desde `initialBilletes` por `value`. Se
  // reevalúa cada vez que cambia el prop → si el usuario cierra sin
  // guardar y vuelve a abrir con otro arqueo, se re-hidrata.
  const buildInitialRows = React.useCallback(() => {
    return DENOMINATIONS.map((d) => {
      const match = Array.isArray(initialBilletes)
        ? initialBilletes.find((b) => Number(b.value) === d.value)
        : null;
      return { ...d, unidades: match ? Number(match.unidades) || 0 : 0 };
    });
  }, [initialBilletes]);

  const [billetes, setBilletes] = useState(buildInitialRows);
  const [selected, setSelected] = useState(0);
  const calcRef = useRef({});

  // Al abrirse el diálogo o cambiar los `initialBilletes` re-hidratamos.
  // Sin esto, la 2ª apertura mostraría el estado local viejo (o los 0
  // del reset tras cerrar) en lugar del arqueo actual del store.
  React.useEffect(() => {
    if (open) setBilletes(buildInitialRows());
  }, [open, buildInitialRows]);

  const total = useMemo(
    () => billetes.reduce((sum, b) => sum + b.value * b.unidades, 0),
    [billetes],
  );
  const totalItems = useMemo(
    () => billetes.reduce((sum, b) => sum + b.unidades, 0),
    [billetes],
  );

  const setUnidades = (index, newUnidades) => {
    const clean = Math.max(0, Math.floor(Number(newUnidades) || 0));
    setBilletes((prev) =>
      prev.map((b, i) => (i === index ? { ...b, unidades: clean } : b)),
    );
  };

  const selectRow = (index) => {
    setSelected(index);
    // Reset del display de la calculadora al cambiar de fila para que
    // el usuario vea el input limpio y pueda teclear el nº directo.
    calcRef.current?.resetClickHandlerWithNoCallback?.();
  };

  // La calculadora vuelca su valor final aquí — se aplica a la fila
  // seleccionada (mismo patrón que ArqueoCount).
  const onCalcValue = (importe) => {
    if (importe === null || importe === undefined) return;
    const n = Math.max(0, Math.floor(Number(importe) || 0));
    if (n === 0) return;
    setUnidades(selected, n);
  };

  const reset = () => {
    setBilletes(DENOMINATIONS.map((d) => ({ ...d, unidades: 0 })));
    setSelected(0);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleConfirm = () => {
    const filtered = billetes
      .filter((b) => b.unidades > 0)
      .map((b) => ({ value: b.value, unidades: b.unidades }));
    onConfirm?.({ billetes: filtered, total });
    reset();
  };

  const grupoBilletes = billetes
    .map((b, i) => ({ ...b, index: i }))
    .filter((b) => b.value >= 5);
  const grupoMonedas = billetes
    .map((b, i) => ({ ...b, index: i }))
    .filter((b) => b.value < 5);

  const renderRow = (b) => {
    const subtotal = b.value * b.unidades;
    const isSelected = selected === b.index;
    return (
      <Stack
        key={b.value}
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => selectRow(b.index)}
        sx={{
          py: 0.75,
          px: 1,
          borderRadius: 1,
          cursor: "pointer",
          borderBottom: "1px dashed",
          borderColor: "divider",
          bgcolor: isSelected ? "primary.lighter" : "transparent",
          transition: "background-color .12s",
          "&:hover": {
            bgcolor: isSelected ? "primary.lighter" : "action.hover",
          },
        }}
      >
        <img
          src={b.img}
          alt={b.label}
          style={{ width: 44, height: 28, objectFit: "contain" }}
        />
        <Typography sx={{ width: 42, fontWeight: 600 }}>{b.label}</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setUnidades(b.index, b.unidades - 1);
          }}
          disabled={b.unidades === 0}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <TextField
          value={b.unidades}
          onChange={(e) => setUnidades(b.index, parseInt(e.target.value, 10))}
          onClick={(e) => {
            e.stopPropagation();
            selectRow(b.index);
          }}
          type="number"
          size="small"
          inputProps={{
            min: 0,
            style: { textAlign: "center", padding: "6px 0", width: 40 },
          }}
          sx={{ width: 56 }}
        />
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setUnidades(b.index, b.unidades + 1);
          }}
        >
          <AddIcon fontSize="small" />
        </IconButton>
        <Typography
          sx={{
            width: 76,
            textAlign: "right",
            fontWeight: 600,
            color: subtotal > 0 ? "text.primary" : "text.disabled",
          }}
        >
          {subtotal.toFixed(2)}€
        </Typography>
      </Stack>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <PointOfSaleIcon color="primary" />
        <Typography variant="h6" sx={{ flex: 1 }}>
          Contar dinero al abrir la caja
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {totalItems} {totalItems === 1 ? "pieza" : "piezas"}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 1.5, md: 2.5 } }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Selecciona una denominación y usa la calculadora para introducir las
          unidades, o edítalas con los botones ±. El total se guarda como
          efectivo inicial y sale en el ticket X/Z para trazabilidad.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
          }}
        >
          {/* Denominaciones — split billetes/monedas */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              columnGap: 4,
              rowGap: 1,
              alignContent: "start",
            }}
          >
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Billetes
              </Typography>
              {grupoBilletes.map(renderRow)}
            </Box>
            <Box>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Monedas
              </Typography>
              {grupoMonedas.map(renderRow)}
            </Box>
          </Box>

          {/* Calculadora — solo desktop, sticky (mismo patrón ArqueoCount) */}
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              width: 300,
              flexShrink: 0,
            }}
          >
            <Card variant="outlined" sx={{ p: 2, position: "sticky", top: 8 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: "block" }}
              >
                Calculadora
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1 }}
              >
                Selecciona una fila y teclea las unidades.
              </Typography>
              <Calculator
                callback={onCalcValue}
                ref={calcRef}
                justNumber={true}
                isMobile={false}
              />
            </Card>
          </Box>
        </Box>
      </DialogContent>

      {/* Barra de acciones fija — total prominente + botones */}
      <DialogActions
        sx={{ p: { xs: 1.5, md: 2 }, borderTop: 1, borderColor: "divider" }}
      >
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              px: 1.5,
              py: 1,
              mb: 1.5,
              borderRadius: 1.5,
              bgcolor: (t) =>
                total > 0
                  ? t.palette.success.lighter || "#E8F5E9"
                  : t.palette.grey[100],
              border: 1,
              borderColor: total > 0 ? "success.light" : "divider",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckCircleOutlineIcon
                color={total > 0 ? "success" : "disabled"}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Total contado
              </Typography>
            </Stack>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                color: total > 0 ? "success.dark" : "text.disabled",
                fontVariant: "tabular-nums",
              }}
            >
              {total.toFixed(2)}€
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.25} sx={{ "& > button": { flex: 1 } }}>
            <Button
              variant="outlined"
              color="inherit"
              size="large"
              onClick={handleClose}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={handleConfirm}
              disabled={total <= 0}
              startIcon={<CheckCircleOutlineIcon />}
            >
              Guardar dinero inicial
            </Button>
          </Stack>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
